import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Form,
    FormFeedback,
    FormGroup,
    Input,
    Label,
    Row,
    Spinner,
    Table,
    Progress,
} from 'reactstrap';
import { FaCheckCircle, FaPlug, FaSave, FaSyncAlt, FaWordpress } from 'react-icons/fa';
import Swal from 'sweetalert2';
import {
    getCurrentWooCommerceProductSynchronizationJob,
    getWooCommerceConfiguration,
    getWooCommerceProductSynchronizationJob,
    getWooCommerceProductSynchronizationJobErrors,
    saveWooCommerceConfiguration,
    startWooCommerceProductSynchronizationJob,
    synchronizeWooCommerceCategories,
    testWooCommerceConnection,
} from '../../services/woocommerceConfigurationService';
import { createProductSynchronizationPoller } from './productSynchronizationPolling';

const EMPTY_CONFIGURATION = {
    configured: false,
    storeUrl: '',
    consumerKey: '',
    secretConfigured: false,
    webhookSecretConfigured: false,
    synchronizationEnabled: false,
    connectionStatus: 'NOT_TESTED',
    lastConnectionTestAt: null,
    lastConnectionError: null,
};

const STATUS_CONFIG = {
    NOT_TESTED: { color: 'secondary', label: 'Non testée' },
    CONNECTED: { color: 'success', label: 'Connectée' },
    FAILED: { color: 'danger', label: 'Échec' },
};

const getErrorMessage = (error, fallback) =>
    error?.response?.data?.message || fallback;

const normalizeText = (value) =>
    (typeof value === 'string' ? value : '').trim();
const normalizeUrl = (value) =>
    normalizeText(value).replace(/\/+$/, '');

const ACTIVE_JOB_STATUSES = ['PENDING', 'RUNNING', 'CANCEL_REQUESTED'];
const TERMINAL_JOB_STATUSES = [
    'COMPLETED',
    'COMPLETED_WITH_ERRORS',
    'FAILED',
    'CANCELLED',
    'INTERRUPTED',
];
const JOB_STATUS_LABELS = {
    PENDING: 'En attente',
    RUNNING: 'En cours',
    CANCEL_REQUESTED: 'Annulation demandée',
    INTERRUPTED: 'Interrompue',
    COMPLETED: 'Terminée',
    COMPLETED_WITH_ERRORS: 'Terminée avec des erreurs',
    FAILED: 'Échec',
    CANCELLED: 'Annulée',
};

const formatElapsedTime = (startedAt, completedAt) => {
    if (!startedAt) {
        return '-';
    }
    const start = new Date(startedAt.replace(' ', 'T')).getTime();
    const end = completedAt
        ? new Date(completedAt.replace(' ', 'T')).getTime()
        : Date.now();
    const totalSeconds = Math.max(0, Math.floor((end - start) / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return [
        hours > 0 ? `${hours} h` : null,
        minutes > 0 ? `${minutes} min` : null,
        `${seconds} s`,
    ].filter(Boolean).join(' ');
};

const WooCommerceConfigurationPage = () => {
    const consumerSecretRef = useRef(null);
    const webhookSecretRef = useRef(null);
    const [configuration, setConfiguration] = useState(EMPTY_CONFIGURATION);
    const [savedConfiguration, setSavedConfiguration] = useState(EMPTY_CONFIGURATION);
    const [secretChanged, setSecretChanged] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [synchronizingCategories, setSynchronizingCategories] = useState(false);
    const [synchronizingProducts, setSynchronizingProducts] = useState(false);
    const [categorySyncSummary, setCategorySyncSummary] = useState(null);
    const [productSyncSummary, setProductSyncSummary] = useState(null);
    const [productSyncProgress, setProductSyncProgress] = useState(null);
    const [productSyncErrors, setProductSyncErrors] = useState([]);
    const productPollerRef = useRef(null);
    const componentMountedRef = useRef(true);
    const completionNotificationJobRef = useRef(null);

    const stopProductPolling = useCallback(() => {
        productPollerRef.current?.stop();
        productPollerRef.current = null;
    }, []);

    const loadProductJobErrors = useCallback(async (jobId) => {
        try {
            const { data } =
                await getWooCommerceProductSynchronizationJobErrors(jobId);
            if (componentMountedRef.current) {
                setProductSyncErrors(data?.content || []);
            }
        } catch {
            // The job summary remains available even if error details cannot be loaded.
        }
    }, []);

    const applyProductJob = useCallback((job, notifyCompletion = false) => {
        if (!componentMountedRef.current || !job) {
            return;
        }
        const terminal = TERMINAL_JOB_STATUSES.includes(job.status);
        setProductSyncProgress(job);
        setSynchronizingProducts(!terminal);
        if (!terminal) {
            return;
        }

        stopProductPolling();
        setProductSyncSummary(job);
        if (job.failed > 0) {
            loadProductJobErrors(job.jobId);
        } else {
            setProductSyncErrors([]);
        }

        if (
            notifyCompletion &&
            completionNotificationJobRef.current !== job.jobId
        ) {
            completionNotificationJobRef.current = job.jobId;
            if (job.status === 'FAILED') {
                Swal.fire({
                    icon: 'error',
                    title: 'Échec de la synchronisation',
                    text:
                        job.globalError ||
                        'La synchronisation des produits WooCommerce a échoué.',
                });
            } else if (job.status === 'COMPLETED_WITH_ERRORS') {
                Swal.fire({
                    icon: 'warning',
                    title: 'Synchronisation terminée avec des erreurs',
                    text: `${job.processed} produit(s) traité(s), ${job.failed} échec(s).`,
                });
            } else {
                Swal.fire({
                    icon: 'success',
                    title: 'Produits synchronisés',
                    text: `${job.processed} produit(s) traité(s) avec succès.`,
                    showConfirmButton: false,
                    timer: 2000,
                });
            }
        }
    }, [loadProductJobErrors, stopProductPolling]);

    const startProductPolling = useCallback((jobId, notifyCompletion = true) => {
        stopProductPolling();
        productPollerRef.current = createProductSynchronizationPoller({
            fetchJob: async currentJobId => {
                const { data } =
                    await getWooCommerceProductSynchronizationJob(currentJobId);
                return data;
            },
            onUpdate: job => {
                if (!TERMINAL_JOB_STATUSES.includes(job.status)) {
                    applyProductJob(job, false);
                }
            },
            onTerminal: job => applyProductJob(job, notifyCompletion),
            onTemporaryError: () => {
                // Temporary polling failures are retried without losing the job ID.
            },
            intervalMs: 2500,
        });
        productPollerRef.current.start(jobId);
    }, [applyProductJob, stopProductPolling]);

    useEffect(() => {
        componentMountedRef.current = true;
        const restoreProductJob = async () => {
            try {
                const { data } =
                    await getCurrentWooCommerceProductSynchronizationJob();
                if (!data || !componentMountedRef.current) {
                    return;
                }
                if (ACTIVE_JOB_STATUSES.includes(data.status)) {
                    setProductSyncProgress(data);
                    setSynchronizingProducts(true);
                    startProductPolling(data.jobId, false);
                } else {
                    applyProductJob(data, false);
                }
            } catch {
                // Configuration loading already provides the page-level error handling.
            }
        };
        restoreProductJob();

        return () => {
            componentMountedRef.current = false;
            stopProductPolling();
        };
    }, [applyProductJob, startProductPolling, stopProductPolling]);

    const loadConfiguration = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await getWooCommerceConfiguration();
            const loaded = {
                ...EMPTY_CONFIGURATION,
                ...data,
                storeUrl: data?.storeUrl || '',
                consumerKey: data?.consumerKey || '',
            };
            setConfiguration(loaded);
            setSavedConfiguration(loaded);
            setSecretChanged(false);
            setErrors({});
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: getErrorMessage(
                    error,
                    'Impossible de charger la configuration WooCommerce.'
                ),
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadConfiguration();
    }, [loadConfiguration]);

    const credentialsChanged = useMemo(
        () =>
            normalizeUrl(configuration.storeUrl) !== savedConfiguration.storeUrl ||
            normalizeText(configuration.consumerKey) !== savedConfiguration.consumerKey ||
            secretChanged,
        [configuration, savedConfiguration, secretChanged]
    );

    const displayedStatus = credentialsChanged
        ? 'NOT_TESTED'
        : configuration.connectionStatus;
    const hasUnsavedChanges =
        credentialsChanged ||
        configuration.synchronizationEnabled !==
            savedConfiguration.synchronizationEnabled;
    const status = STATUS_CONFIG[displayedStatus] || STATUS_CONFIG.NOT_TESTED;
    const synchronizationCanBeEnabled =
        configuration.configured &&
        normalizeText(configuration.consumerKey).startsWith('ck_') &&
        configuration.secretConfigured &&
        displayedStatus === 'CONNECTED';

    const handleChange = (event) => {
        const { name, value } = event.target;
        setConfiguration(previous => ({
            ...previous,
            [name]: value,
            ...(['storeUrl', 'consumerKey'].includes(name)
                ? { synchronizationEnabled: false }
                : {}),
        }));
        setErrors(previous => ({ ...previous, [name]: undefined }));
    };

    const handleSecretChange = () => {
        setSecretChanged(Boolean(consumerSecretRef.current?.value));
        setConfiguration(previous => ({
            ...previous,
            synchronizationEnabled: false,
        }));
        setErrors(previous => ({ ...previous, consumerSecret: undefined }));
    };

    const validate = () => {
        const nextErrors = {};
        const storeUrl = normalizeUrl(configuration.storeUrl);
        const consumerKey = normalizeText(configuration.consumerKey);
        const consumerSecret = normalizeText(consumerSecretRef.current?.value);

        if (!storeUrl) {
            nextErrors.storeUrl = "L'URL de la boutique est obligatoire.";
        } else {
            try {
                const url = new URL(storeUrl);
                if (!['http:', 'https:'].includes(url.protocol)) {
                    nextErrors.storeUrl = "L'URL doit commencer par http:// ou https://.";
                } else if (url.search || url.hash || storeUrl.toLowerCase().includes('/wp-json/')) {
                    nextErrors.storeUrl =
                        "Saisissez uniquement l'URL de base, sans /wp-json/wc/v3.";
                }
            } catch {
                nextErrors.storeUrl = "L'URL de la boutique n'est pas valide.";
            }
        }

        if (!consumerKey) {
            nextErrors.consumerKey = 'Le Consumer Key est obligatoire.';
        } else if (!consumerKey.startsWith('ck_')) {
            nextErrors.consumerKey = 'Le Consumer Key doit commencer par ck_.';
        }

        if (!configuration.secretConfigured && !consumerSecret) {
            nextErrors.consumerSecret =
                'Le Consumer Secret est obligatoire lors de la première configuration.';
        } else if (consumerSecret && !consumerSecret.startsWith('cs_')) {
            nextErrors.consumerSecret = 'Le Consumer Secret doit commencer par cs_.';
        }

        if (configuration.synchronizationEnabled && !synchronizationCanBeEnabled) {
            nextErrors.synchronizationEnabled =
                'Testez la connexion avec succès avant d’activer la synchronisation.';
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSave = async (event) => {
        event.preventDefault();
        if (!validate()) {
            return;
        }

        setSaving(true);
        try {
            await saveWooCommerceConfiguration({
                storeUrl: normalizeUrl(configuration.storeUrl),
                consumerKey: normalizeText(configuration.consumerKey),
                consumerSecret: normalizeText(consumerSecretRef.current?.value),
                webhookSecret: normalizeText(webhookSecretRef.current?.value),
                synchronizationEnabled: configuration.synchronizationEnabled,
            });
            await loadConfiguration();
            Swal.fire({
                icon: 'success',
                title: 'Configuration enregistrée',
                text: 'La configuration WooCommerce a été enregistrée avec succès.',
                showConfirmButton: false,
                timer: 1800,
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: "Erreur d'enregistrement",
                text: getErrorMessage(
                    error,
                    "Impossible d'enregistrer la configuration WooCommerce."
                ),
            });
        } finally {
            setSaving(false);
        }
    };

    const handleTestConnection = async () => {
        if (credentialsChanged) {
            Swal.fire({
                icon: 'info',
                title: 'Enregistrez vos modifications',
                text: 'La connexion utilise uniquement la configuration enregistrée.',
            });
            return;
        }

        setTesting(true);
        try {
            const { data } = await testWooCommerceConnection();
            await loadConfiguration();
            if (data.connectionStatus === 'CONNECTED') {
                Swal.fire({
                    icon: 'success',
                    title: 'Connexion réussie',
                    text: 'Connexion à WooCommerce établie avec succès.',
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Échec de connexion',
                    text:
                        data.lastConnectionError ||
                        "Impossible de se connecter à WooCommerce. Vérifiez l'URL de la boutique et les identifiants API.",
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Échec de connexion',
                text: getErrorMessage(
                    error,
                    "Impossible de se connecter à WooCommerce. Vérifiez l'URL de la boutique et les identifiants API."
                ),
            });
        } finally {
            setTesting(false);
        }
    };

    const handleSynchronizationChange = (event) => {
        const enabled = event.target.checked;
        if (enabled && !synchronizationCanBeEnabled) {
            setErrors(previous => ({
                ...previous,
                synchronizationEnabled:
                    'Testez la connexion avec succès avant d’activer la synchronisation.',
            }));
            return;
        }
        setErrors(previous => ({ ...previous, synchronizationEnabled: undefined }));
        setConfiguration(previous => ({
            ...previous,
            synchronizationEnabled: enabled,
        }));
    };

    const handleCategorySynchronization = async () => {
        const confirmation = await Swal.fire({
            icon: 'question',
            title: 'Synchroniser les catégories ?',
            text: 'Les catégories actives de l’ERP seront créées ou mises à jour dans WooCommerce.',
            showCancelButton: true,
            confirmButtonText: 'Synchroniser',
            cancelButtonText: 'Annuler',
        });
        if (!confirmation.isConfirmed) {
            return;
        }

        setSynchronizingCategories(true);
        setCategorySyncSummary(null);
        try {
            await synchronizeWooCommerceCategories();
            Swal.fire({
                icon: 'info',
                title: 'Synchronisation lancée',
                text: 'Le traitement continue en arrière-plan. Suivez sa progression dans le centre des traitements.',
                showConfirmButton: false,
                timer: 3000,
            });
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Échec de la synchronisation',
                text: getErrorMessage(
                    error,
                    'Impossible de synchroniser les catégories avec WooCommerce.'
                ),
            });
        } finally {
            setSynchronizingCategories(false);
        }
    };

    const handleProductSynchronization = async () => {
        const confirmation = await Swal.fire({
            icon: 'question',
            title: 'Synchroniser les produits ?',
            text: 'Les produits ERP éligibles seront créés ou mis à jour dans WooCommerce. Les stocks et les images ne seront pas synchronisés.',
            showCancelButton: true,
            confirmButtonText: 'Synchroniser',
            cancelButtonText: 'Annuler',
        });
        if (!confirmation.isConfirmed) {
            return;
        }

        setSynchronizingProducts(true);
        setProductSyncSummary(null);
        setProductSyncErrors([]);
        completionNotificationJobRef.current = null;
        setProductSyncProgress({
            status: 'PENDING',
            total: 0,
            processed: 0,
            progressPercentage: 0,
        });
        try {
            const { data } =
                await startWooCommerceProductSynchronizationJob();
            setProductSyncProgress(previous => ({
                ...previous,
                jobId: data.jobId,
                status: data.status,
            }));
            startProductPolling(data.jobId, false);
        } catch (error) {
            setSynchronizingProducts(false);
            Swal.fire({
                icon: 'error',
                title: 'Échec de la synchronisation',
                text: getErrorMessage(
                    error,
                    'Impossible de synchroniser les produits avec WooCommerce.'
                ),
            });
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <Spinner color="primary" />
                <span className="ms-2">Chargement de la configuration WooCommerce...</span>
            </div>
        );
    }

    return (
        <div className="container-fluid px-2 px-md-3">
            <Card className="shadow-sm border-0 mb-3">
                <CardBody className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                    <div>
                        <h4 className="mb-1 d-flex align-items-center">
                            <FaWordpress className="text-primary me-2" />
                            Configuration WooCommerce
                        </h4>
                        <div className="text-muted">
                            Configurez la connexion et suivez les synchronisations manuelles
                            de votre boutique.
                        </div>
                    </div>
                    <div className="text-md-end">
                        <div className="small text-muted mb-1">État de la connexion</div>
                        <Badge color={status.color} pill className="px-3 py-2">
                            {status.label}
                        </Badge>
                    </div>
                </CardBody>
            </Card>

            <Row className="g-3">
                <Col xl={8}>
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            <Form onSubmit={handleSave} noValidate>
                                <Row>
                                    <Col xs={12}>
                                        <FormGroup>
                                            <Label for="storeUrl">URL de la boutique</Label>
                                            <Input
                                                id="storeUrl"
                                                name="storeUrl"
                                                type="url"
                                                placeholder="https://cima.ma"
                                                value={configuration.storeUrl}
                                                onChange={handleChange}
                                                invalid={Boolean(errors.storeUrl)}
                                                disabled={
                                                    saving ||
                                                    testing ||
                                                    synchronizingCategories ||
                                                    synchronizingProducts
                                                }
                                            />
                                            <FormFeedback>{errors.storeUrl}</FormFeedback>
                                            <div className="form-text">
                                                Utilisez uniquement l’URL de base, sans
                                                {' '}
                                                <code>/wp-json/wc/v3</code>.
                                            </div>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="consumerKey">Consumer Key</Label>
                                            <Input
                                                id="consumerKey"
                                                name="consumerKey"
                                                value={configuration.consumerKey}
                                                onChange={handleChange}
                                                placeholder="ck_..."
                                                autoComplete="off"
                                                invalid={Boolean(errors.consumerKey)}
                                                disabled={saving || testing || synchronizingCategories}
                                            />
                                            <FormFeedback>{errors.consumerKey}</FormFeedback>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="consumerSecret">Consumer Secret</Label>
                                            <Input
                                                id="consumerSecret"
                                                name="consumerSecret"
                                                type="password"
                                                innerRef={consumerSecretRef}
                                                onChange={handleSecretChange}
                                                placeholder={
                                                    configuration.secretConfigured
                                                        ? '••••••••••••••••'
                                                        : 'cs_...'
                                                }
                                                autoComplete="new-password"
                                                invalid={Boolean(errors.consumerSecret)}
                                                disabled={saving || testing || synchronizingCategories}
                                            />
                                            <FormFeedback>{errors.consumerSecret}</FormFeedback>
                                            {configuration.secretConfigured && (
                                                <div className="form-text">
                                                    Laissez ce champ vide pour conserver le Consumer
                                                    Secret existant.
                                                </div>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="webhookSecret">Secret du webhook commandes</Label>
                                            <Input
                                                id="webhookSecret"
                                                type="password"
                                                innerRef={webhookSecretRef}
                                                placeholder={configuration.webhookSecretConfigured ? '••••••••••••••••' : 'Secret WooCommerce'}
                                                autoComplete="new-password"
                                                disabled={saving || testing || synchronizingCategories}
                                            />
                                            {configuration.webhookSecretConfigured && (
                                                <div className="form-text">Laissez vide pour conserver le secret existant.</div>
                                            )}
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <hr />

                                <FormGroup switch className="mb-2">
                                    <Input
                                        id="synchronizationEnabled"
                                        type="switch"
                                        checked={configuration.synchronizationEnabled}
                                        onChange={handleSynchronizationChange}
                                        disabled={
                                            saving ||
                                            testing ||
                                            synchronizingCategories ||
                                            synchronizingProducts ||
                                            (!configuration.synchronizationEnabled &&
                                                !synchronizationCanBeEnabled)
                                        }
                                    />
                                    <Label for="synchronizationEnabled" check>
                                        Activer la synchronisation
                                    </Label>
                                </FormGroup>
                                <div className="form-text mb-1">
                                    La synchronisation pourra être activée après un test de
                                    connexion réussi. Les synchronisations restent déclenchées
                                    manuellement.
                                </div>
                                {errors.synchronizationEnabled && (
                                    <div className="text-danger small mb-3">
                                        {errors.synchronizationEnabled}
                                    </div>
                                )}

                                <div className="d-flex flex-wrap gap-2 mt-4">
                                    <Button
                                        color="primary"
                                        type="submit"
                                        disabled={
                                            saving ||
                                            testing ||
                                            synchronizingCategories ||
                                            synchronizingProducts
                                        }
                                    >
                                        {saving ? (
                                            <Spinner size="sm" className="me-2" />
                                        ) : (
                                            <FaSave className="me-2" />
                                        )}
                                        Enregistrer
                                    </Button>
                                    <Button
                                        color="success"
                                        type="button"
                                        outline
                                        onClick={handleTestConnection}
                                        disabled={
                                            saving ||
                                            testing ||
                                            synchronizingCategories ||
                                            synchronizingProducts ||
                                            !configuration.configured ||
                                            credentialsChanged
                                        }
                                    >
                                        {testing ? (
                                            <Spinner size="sm" className="me-2" />
                                        ) : (
                                            <FaPlug className="me-2" />
                                        )}
                                        Tester la connexion
                                    </Button>
                                    <Button
                                        color="info"
                                        type="button"
                                        onClick={handleCategorySynchronization}
                                        disabled={
                                            saving ||
                                            testing ||
                                            synchronizingCategories ||
                                            synchronizingProducts ||
                                            hasUnsavedChanges ||
                                            !configuration.configured ||
                                            !configuration.synchronizationEnabled ||
                                            displayedStatus !== 'CONNECTED'
                                        }
                                    >
                                        {synchronizingCategories ? (
                                            <Spinner size="sm" className="me-2" />
                                        ) : (
                                            <FaSyncAlt className="me-2" />
                                        )}
                                        Synchroniser les catégories
                                    </Button>
                                    <Button
                                        color="warning"
                                        type="button"
                                        onClick={handleProductSynchronization}
                                        disabled={
                                            saving ||
                                            testing ||
                                            synchronizingCategories ||
                                            synchronizingProducts ||
                                            hasUnsavedChanges ||
                                            !configuration.configured ||
                                            !configuration.synchronizationEnabled ||
                                            displayedStatus !== 'CONNECTED'
                                        }
                                    >
                                        {synchronizingProducts ? (
                                            <Spinner size="sm" className="me-2" />
                                        ) : (
                                            <FaSyncAlt className="me-2" />
                                        )}
                                        Synchroniser les produits
                                    </Button>
                                </div>
                                {synchronizingCategories && (
                                    <Alert color="info" className="mt-3 mb-0">
                                        <Spinner size="sm" className="me-2" />
                                        Synchronisation des catégories en cours…
                                    </Alert>
                                )}
                                {productSyncProgress && (
                                    <Alert
                                        color={
                                            productSyncProgress.status === 'FAILED'
                                                ? 'danger'
                                                : productSyncProgress.status ===
                                                    'COMPLETED_WITH_ERRORS'
                                                    ? 'warning'
                                                    : productSyncProgress.status === 'COMPLETED'
                                                        ? 'success'
                                                        : 'info'
                                        }
                                        className="mt-3 mb-0"
                                    >
                                        <div className="d-flex justify-content-between mb-2">
                                            <span>
                                                {synchronizingProducts && (
                                                    <Spinner size="sm" className="me-2" />
                                                )}
                                                Synchronisation des produits —{' '}
                                                {JOB_STATUS_LABELS[
                                                    productSyncProgress.status
                                                ] || productSyncProgress.status}
                                            </span>
                                            <strong>
                                                {Number(
                                                    productSyncProgress.progressPercentage || 0
                                                ).toFixed(2)}{' '}
                                                %
                                            </strong>
                                        </div>
                                        <Progress
                                            animated={synchronizingProducts}
                                            striped={synchronizingProducts}
                                            color={
                                                productSyncProgress.status === 'FAILED'
                                                    ? 'danger'
                                                    : productSyncProgress.status === 'COMPLETED'
                                                        ? 'success'
                                                        : 'info'
                                            }
                                            value={
                                                productSyncProgress.progressPercentage || 0
                                            }
                                            aria-label="Progression de la synchronisation des produits"
                                        >
                                            {Number(
                                                productSyncProgress.progressPercentage || 0
                                            ).toFixed(2)}{' '}
                                            %
                                        </Progress>
                                        <div className="small mt-3">
                                            <div>
                                                {productSyncProgress.processed || 0} /{' '}
                                                {productSyncProgress.total || 0} produit(s) traité(s)
                                            </div>
                                            <div className="d-flex flex-wrap gap-3 mt-1">
                                                <span>Créés : {productSyncProgress.created || 0}</span>
                                                <span>Mis à jour : {productSyncProgress.updated || 0}</span>
                                                <span>Inchangés : {productSyncProgress.skipped || 0}</span>
                                                <span>Échecs : {productSyncProgress.failed || 0}</span>
                                            </div>
                                            {(productSyncProgress.currentProductSku ||
                                                productSyncProgress.currentProductName) && (
                                                <div className="mt-1">
                                                    Produit courant :{' '}
                                                    {productSyncProgress.currentProductSku || '-'}
                                                    {' — '}
                                                    {productSyncProgress.currentProductName || '-'}
                                                </div>
                                            )}
                                            <div className="mt-1">
                                                Début :{' '}
                                                {productSyncProgress.startedAt
                                                    ? new Date(
                                                        productSyncProgress.startedAt.replace(
                                                            ' ',
                                                            'T'
                                                        )
                                                    ).toLocaleString('fr-FR')
                                                    : '-'}
                                            </div>
                                            <div className="mt-1">
                                                Durée :{' '}
                                                {formatElapsedTime(
                                                    productSyncProgress.startedAt,
                                                    productSyncProgress.completedAt
                                                )}
                                            </div>
                                            {productSyncProgress.globalError && (
                                                <div className="text-danger mt-2">
                                                    {productSyncProgress.globalError}
                                                </div>
                                            )}
                                        </div>
                                    </Alert>
                                )}
                            </Form>
                        </CardBody>
                    </Card>
                </Col>

                <Col xl={4}>
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            <h5 className="mb-3">Dernier test</h5>
                            <dl className="mb-0">
                                <dt>Statut</dt>
                                <dd>
                                    <Badge color={status.color}>{status.label}</Badge>
                                </dd>
                                <dt>Date du test</dt>
                                <dd>
                                    {configuration.lastConnectionTestAt
                                        ? new Date(
                                            configuration.lastConnectionTestAt.replace(' ', 'T')
                                        ).toLocaleString('fr-FR')
                                        : 'Aucun test effectué'}
                                </dd>
                            </dl>

                            {configuration.lastConnectionError && !credentialsChanged && (
                                <Alert color="danger" className="mb-0 mt-3">
                                    {configuration.lastConnectionError}
                                </Alert>
                            )}
                            {displayedStatus === 'CONNECTED' && (
                                <Alert color="success" className="mb-0 mt-3">
                                    <FaCheckCircle className="me-2" />
                                    La configuration enregistrée est opérationnelle.
                                </Alert>
                            )}
                            {credentialsChanged && (
                                <Alert color="info" className="mb-0 mt-3">
                                    Enregistrez les identifiants modifiés avant de tester la
                                    connexion.
                                </Alert>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {categorySyncSummary && (
                <Card className="shadow-sm mt-3">
                    <CardBody>
                        <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mb-3">
                            <div>
                                <h5 className="mb-1">Résumé de la synchronisation</h5>
                                <div className="text-muted small">
                                    Dernière exécution :{' '}
                                    {new Date(
                                        categorySyncSummary.finishedAt.replace(' ', 'T')
                                    ).toLocaleString('fr-FR')}
                                </div>
                            </div>
                            <div className="d-flex flex-wrap gap-2 align-items-start">
                                <Badge color="primary" className="px-3 py-2">
                                    Traitées : {categorySyncSummary.processed}
                                </Badge>
                                <Badge color="success" className="px-3 py-2">
                                    Créées : {categorySyncSummary.created}
                                </Badge>
                                <Badge color="info" className="px-3 py-2">
                                    Mises à jour : {categorySyncSummary.updated}
                                </Badge>
                                <Badge color="secondary" className="px-3 py-2">
                                    Ignorées : {categorySyncSummary.skipped}
                                </Badge>
                                <Badge
                                    color={categorySyncSummary.failed ? 'danger' : 'success'}
                                    className="px-3 py-2"
                                >
                                    Échecs : {categorySyncSummary.failed}
                                </Badge>
                            </div>
                        </div>

                        {categorySyncSummary.failures?.length > 0 && (
                            <div className="table-responsive">
                                <Table bordered hover size="sm" className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Code ERP</th>
                                            <th>Catégorie</th>
                                            <th>Erreur</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categorySyncSummary.failures.map(failure => (
                                            <tr key={failure.categoryId}>
                                                <td>{failure.categoryCode || '-'}</td>
                                                <td>{failure.categoryName || '-'}</td>
                                                <td className="text-danger">{failure.message}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            )}

            {productSyncSummary && (
                <Card className="shadow-sm mt-3">
                    <CardBody>
                        <div className="d-flex flex-column flex-md-row justify-content-between gap-2 mb-3">
                            <div>
                                <h5 className="mb-1">Résumé de la synchronisation des produits</h5>
                                <div className="text-muted small">
                                    Dernière exécution :{' '}
                                    {productSyncSummary.completedAt
                                        ? new Date(
                                            productSyncSummary.completedAt.replace(' ', 'T')
                                        ).toLocaleString('fr-FR')
                                        : '-'}
                                </div>
                            </div>
                            <div className="d-flex flex-wrap gap-2 align-items-start">
                                <Badge color="primary" className="px-3 py-2">
                                    Traités : {productSyncSummary.processed}
                                </Badge>
                                <Badge color="success" className="px-3 py-2">
                                    Créés : {productSyncSummary.created}
                                </Badge>
                                <Badge color="info" className="px-3 py-2">
                                    Mis à jour : {productSyncSummary.updated}
                                </Badge>
                                <Badge color="secondary" className="px-3 py-2">
                                    Inchangés : {productSyncSummary.skipped}
                                </Badge>
                                <Badge
                                    color={productSyncSummary.failed ? 'danger' : 'success'}
                                    className="px-3 py-2"
                                >
                                    Échecs : {productSyncSummary.failed}
                                </Badge>
                            </div>
                        </div>

                        {productSyncErrors.length > 0 && (
                            <div className="table-responsive">
                                <Table bordered hover size="sm" className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>SKU</th>
                                            <th>Produit</th>
                                            <th>Erreur</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productSyncErrors.map(error => (
                                            <tr key={error.id}>
                                                <td>{error.sku || '-'}</td>
                                                <td>{error.name || '-'}</td>
                                                <td className="text-danger">{error.message}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            )}
        </div>
    );
};

export default WooCommerceConfigurationPage;
